import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import { useOrganizationStore } from '../../stores/organizationStore';
import { useToast } from '../../components/ui';
import { extractApiErrorMessage } from '../../utils/apiError';
import { LoginCredentials } from '../../types/api';

/**
 * Hook for login functionality
 */
export const useLogin = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { login } = useAuthStore();
  const { clearOrganization } = useOrganizationStore();

  return useMutation({
    mutationFn: ({ username, password }: LoginCredentials) => 
      login({ username, password }),
    onSuccess: () => {
      // Clear any previous organization data
      clearOrganization();
      
      // Get the user from the store after successful login
      const user = useAuthStore.getState().user;
      
      // Show success message
      addToast({
        title: 'Welcome back!',
        message: `Logged in as ${user?.first_name} ${user?.last_name}`,
        type: 'success',
      });
      
      // Navigate to dashboard
      navigate('/dashboard');
    },
    onError: (error: any) => {
      console.error('Login hook error:', error);
      addToast({
        title: 'Login failed',
        message: extractApiErrorMessage(error, 'Invalid username or password'),
        type: 'error',
      });
    },
  });
};

/**
 * Hook for logout functionality
 */
export const useLogout = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { clearAuth } = useAuthStore();
  const { clearOrganization } = useOrganizationStore();

  return useMutation({
    mutationFn: authApi.logout,
    onSuccess: () => {
      // Clear all auth and organization data
      clearAuth();
      clearOrganization();
      
      // Show message
      addToast({
        title: 'Logged out',
        message: 'You have been successfully logged out',
        type: 'info',
      });
      
      // Navigate to login
      navigate('/login');
    },
    onError: () => {
      // Even if logout fails on server, clear local state
      clearAuth();
      clearOrganization();
      navigate('/login');
    },
  });
};

/**
 * Hook for validating current token
 */
export const useValidateToken = () => {
  const { setUser, clearAuth } = useAuthStore();

  return useMutation({
    mutationFn: authApi.testToken,
    onSuccess: (user) => {
      // Update user data if token is valid
      setUser(user);
    },
    onError: () => {
      // Clear auth if token is invalid
      clearAuth();
    },
  });
};
